import { useCallback, useMemo, useState } from 'react';

/**
 * The chip search, as state.
 *
 * ── The interaction, kept; the mechanism, replaced ───────────────────
 *
 * The old portal's chips were its signature interaction and its worst bug at the
 * same time. Type a term, press Enter, it becomes a chip; chips AND together;
 * clicking one turns it off without deleting it; the × removes it. All of that
 * is worth keeping.
 *
 * What is not worth keeping is what it filtered: rows already fetched. A search
 * over the first page reports "no match" for a record that exists on the second,
 * with no error and nothing on screen suggesting the answer is partial. Phase 6b
 * fixed that for Users by moving search to the server, and this is the same fix
 * with the old interface on top of it.
 *
 * So this hook holds terms and nothing else. It does no filtering, has no
 * opinion about rows, and never sees them. `active` is the value a screen passes
 * to `useAsync`'s dependency list, which is what turns every chip change into a
 * new request.
 *
 * ── Why a disabled chip is not a deleted one ─────────────────────────
 *
 * Narrowing a search too far is the common case, and the fix is almost always
 * "take that one off for a second". Deleting it means retyping it. Toggling it
 * off has to refetch like everything else — a disabled chip that filtered
 * locally would make the two paths disagree, which is the bug this replaced.
 */
export type Chip = { term: string; enabled: boolean };

export type ChipSearch = {
  chips: Chip[];
  /** The terms currently in force. This is the fetch key — pass it to `useAsync`. */
  active: string[];
  /**
   * A stable string for the dependency array.
   *
   * `active` is a new array every render, so depending on it directly refetches
   * forever. Joined with a character a search term cannot contain — a chip is
   * committed from a single-line input, so a newline is unreachable — rather
   * than a comma, which is a perfectly ordinary thing to search for and would
   * make `["a,b"]` and `["a","b"]` the same key.
   */
  key: string;
  add: (term: string) => void;
  remove: (term: string) => void;
  toggle: (term: string) => void;
  clear: () => void;
};

/**
 * `onChange` fires on every chip change, and exists for one thing: resetting
 * the page.
 *
 * Staying on page 3 of a result set that now has one page shows an empty table
 * for a search that matched — an empty state that means "you are past the end"
 * while reading as "nothing found". The alternative, an effect watching `key`,
 * would reset offset AFTER a fetch had already gone out with the stale one.
 */
export function useChips(initial: string[] = [], onChange?: () => void): ChipSearch {
  const [chips, setChips] = useState<Chip[]>(() =>
    initial.map((term) => ({ term, enabled: true })),
  );

  const changed = useCallback(() => onChange?.(), [onChange]);

  const add = useCallback((raw: string) => {
    const term = raw.trim();
    if (!term) return;
    changed();
    setChips((cur) =>
      /**
       * A repeat re-enables rather than appending a duplicate.
       *
       * Typing a term you already have is almost always "where did that go" —
       * usually because it is sitting there disabled. Two identical chips would
       * be two identical filters, which changes nothing and cannot be told apart
       * on screen.
       */
      cur.some((c) => c.term === term)
        ? cur.map((c) => (c.term === term ? { ...c, enabled: true } : c))
        : [...cur, { term, enabled: true }],
    );
  }, [changed]);

  const remove = useCallback(
    (term: string) => {
      changed();
      setChips((cur) => cur.filter((c) => c.term !== term));
    },
    [changed],
  );

  const toggle = useCallback(
    (term: string) => {
      changed();
      setChips((cur) => cur.map((c) => (c.term === term ? { ...c, enabled: !c.enabled } : c)));
    },
    [changed],
  );

  const clear = useCallback(() => {
    changed();
    setChips([]);
  }, [changed]);

  const active = useMemo(() => chips.filter((c) => c.enabled).map((c) => c.term), [chips]);

  return { chips, active, key: active.join('\n'), add, remove, toggle, clear };
}
